import React from "react";
import { convertCentsToDollars } from "src/helpers";
import { MenuItemOptionElement } from "src/interfaces/menu.interface";
import { useStorePosLocales, PlatformLocale } from "src/hooks/useStorePosLocales";
import {
  buildLocalizedNameTooltip,
  resolveLocalizedDisplayName,
} from "src/utils/localeTranslations";

interface MenuItemOptionElementRowProps {
  element: MenuItemOptionElement;
  elementIndex: number;
  onEdit: (index: number) => void;
  onDelete: (index: number) => void;
}

const MenuItemOptionElementRow: React.FC<MenuItemOptionElementRowProps> = ({
  element,
  elementIndex,
  onEdit,
  onDelete,
}) => {
  const { enabledLocales, defaultLocale, enabledLocaleOptions } = useStorePosLocales();
  const priceDisplay = element.priceCents != null
    ? convertCentsToDollars(element.priceCents).toFixed(2)
    : "0.00";
  const displayName = resolveLocalizedDisplayName(
    element.translations,
    enabledLocales,
    defaultLocale,
    {
      englishName: element.name,
      nonEnglishName: element.nonEnglishName,
    }
  );
  const name = displayName || "—";
  const tooltip = buildLocalizedNameTooltip(
    element.translations,
    enabledLocales,
    {
      englishName: element.name,
      nonEnglishName: element.nonEnglishName,
    },
    Object.fromEntries(
      enabledLocaleOptions.map((locale: PlatformLocale) => [locale.code, locale.nativeLabel])
    )
  );
  const seq = element.renderSequence ?? "—";

  return (
    <div className="flex items-center justify-between py-1 px-2.5 rounded-md border border-[#F1F1F2] bg-white hover:bg-[#f9fafb] hover:border-[#E5E7EB] transition-colors gap-3 group">
      <div className="flex-1 min-w-0 flex items-center gap-3">
        <span
          className="font-medium text-[#1A1A1A] truncate text-sm"
          title={tooltip !== name ? tooltip : undefined}
        >
          {name}
        </span>
        <span className="text-sm text-[#9B9B9B] shrink-0">${priceDisplay}</span>
        <span className="text-xs text-[#9CA3AF] shrink-0">Seq: {String(seq)}</span>
      </div>
      <div className="flex items-center justify-start gap-0.5 shrink-0">
        <button
          type="button"
          onClick={() => onEdit(elementIndex)}
          className="p-2 rounded-full text-[#6B7280] opacity-80 hover:opacity-100 hover:bg-[#f3f4f6] hover:text-[#111827] transition-colors"
          aria-label="Edit"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            className="w-5 h-5"
          >
            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
          </svg>
        </button>
        <button
          type="button"
          onClick={() => onDelete(elementIndex)}
          className="p-2 rounded-full text-[#6B7280] opacity-80 hover:opacity-100 hover:bg-red-50 hover:text-[#DC2626] transition-colors"
          aria-label="Delete"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            className="w-5 h-5"
          >
            <path
              fillRule="evenodd"
              d="M8.75 1A2.75 2.75 0 006 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 10.23 1.482l.149-.022.841 10.518A2.75 2.75 0 007.596 19h4.807a2.75 2.75 0 002.742-2.53l.841-10.518.149.023a.75.75 0 00.23-1.482A41.03 41.03 0 0014 4.193V3.75A2.75 2.75 0 0011.25 1h-2.5zM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4zM8.58 7.72a.75.75 0 00-1.5.06l.3 7.5a.75.75 0 101.5-.06l-.3-7.5zm4.34.06a.75.75 0 10-1.5-.06l-.3 7.5a.75.75 0 101.5.06l.3-7.5z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default React.memo(MenuItemOptionElementRow);
